import { useKeycloak } from '@react-keycloak/web';
import { FC, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Badge, Box, IconButton } from '@mui/material';

import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import { NotificationResponse } from '@/api/data-contracts';
import useSidemenuStore from '@/app/sidemenu.store';
import {
  NotificationsSideMenu,
  useNotificationToastsStore,
  useNotificationsService,
  useNotificationsStore,
} from '@/features/notifications';
import { queryKeys } from '@/react-query/constants';
import mixpanelEvents from '@/shared/lib/analytics/mixpanel-events';
import useMixpanel from '@/shared/lib/analytics/mixpanel.service';

import NotificationToastContainer from './NotificationToastContainer';
import {
  invalidateOrderCommentQueries,
  isOrderCommentNotificationType,
} from './invalidateOrderCommentQueries';
import { ButtonIconWrapper, ButtonLabel, ButtonText, MenuButton } from './styled';
import useNotificationsWebSocket from './useNotificationsWebSocket';

interface Props {
  isActive?: boolean;
  isExpanded?: boolean;
  disabled?: boolean;
  userType?: string;
}

const NotificationsButton: FC<Props> = ({ isActive, isExpanded, disabled, userType }) => {
  const { t } = useTranslation();
  const { trackEvent } = useMixpanel();
  const { getNotifications, getNotificationsCount } = useNotificationsService();
  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();

  const dnd = useNotificationsStore(useShallow((state) => state.dnd));
  const setDnd = useNotificationsStore(useShallow((state) => state.setDnd));
  const addToast = useNotificationToastsStore((s) => s.addToast);
  const clearToasts = useNotificationToastsStore((s) => s.clearAll);

  const targetModule = useMemo(() => {
    if (!userType) return undefined;
    const moduleMap: Record<string, string> = {
      location: 'LOCATION',
      advertiser: 'ADVERTISER',
      backoffice: 'BACKOFFICE',
      service: 'SERVICES',
    };
    return moduleMap[userType];
  }, [userType]);

  const isDrawerOpen = useNotificationsStore(useShallow((state) => state.isDrawerOpen));
  const isDrawerExpanded = useNotificationsStore(useShallow((state) => state.isDrawerExpanded));
  const setIsDrawerOpen = useNotificationsStore(useShallow((state) => state.setIsDrawerOpen));
  const sidebarWidth = useSidemenuStore((state) => state.sidebarWidth);

  const prevUnreadIdsRef = useRef<Set<string>>(new Set());

  const { data: notificationsData } = useInfiniteQuery<
    NotificationResponse[],
    Error,
    NotificationResponse[],
    [string, string, string]
  >({
    queryKey: [queryKeys.notifications, 'unread', targetModule ?? 'all-modules'] as const,
    queryFn: async ({ pageParam = 0, signal }) =>
      getNotifications(
        {
          pageNumber: pageParam / 20,
          numberOfItems: 1000,
          unreadOnly: true,
          ...(targetModule ? { targetModule } : {}),
        },
        signal,
      ),
    getNextPageParam: () => undefined,
    refetchInterval: dnd ? false : 10 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 10 * 1000,
    enabled: !!keycloak.token && navigator.onLine,
  });

  const { data: unreadCount } = useQuery({
    queryKey: [queryKeys.notificationsCount, targetModule ?? 'all-modules'],
    queryFn: ({ signal }) => getNotificationsCount(targetModule, signal),
    refetchInterval: dnd ? false : 10 * 1000,
    refetchIntervalInBackground: false,
    staleTime: 10 * 1000,
    enabled: !!keycloak.token && navigator.onLine,
  });

  const allUnread = useMemo(() => notificationsData?.pages.flat() ?? [], [notificationsData]);

  const unreadNotifications = unreadCount ?? allUnread.length;

  useEffect(() => {
    if (dnd || isDrawerOpen) return;

    const currentIds = new Set(allUnread.map((n) => n.id));
    const prevIds = prevUnreadIdsRef.current;

    if (prevIds.size > 0) {
      const newNotifications = allUnread.filter((n) => !prevIds.has(n.id));
      if (newNotifications.some((n) => n.audit.type === 'ORDER_REQUESTED')) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.orders, 'pending-count'] });
      }
      if (newNotifications.some((n) => isOrderCommentNotificationType(n.audit.type))) {
        invalidateOrderCommentQueries(queryClient);
      }
      newNotifications.forEach((n) => addToast(n));
    }

    prevUnreadIdsRef.current = currentIds;
  }, [allUnread, dnd, isDrawerOpen, addToast, queryClient]);

  useEffect(() => {
    if (dnd) {
      const interval = window.setInterval(() => {
        if (dayjs().isAfter(dnd)) {
          setDnd(undefined);
        }
      }, 60 * 1000);

      return () => window.clearInterval(interval);
    }
  }, [dnd, setDnd]);

  useNotificationsWebSocket({
    token: keycloak.token,
    targetModule,
    queryClient,
  });

  const toggleDrawer = () => {
    if (isDrawerOpen) {
      setIsDrawerOpen(false);
      return;
    }
    trackEvent(mixpanelEvents.viewNotificationsDrawer);
    if (!queryClient.isMutating()) {
      queryClient.invalidateQueries({ queryKey: [queryKeys.notificationsCount] });
    }
    clearToasts();
    setIsDrawerOpen(true);
  };

  return (
    <>
      <MenuButton
        variant="text"
        disabled={disabled}
        isActive={isActive}
        isExpanded={isExpanded}
        data-testid="menu-notifications"
        onClick={toggleDrawer}
        sx={{ overflow: 'visible' }}
      >
        <ButtonLabel isActive={isActive}>
          <ButtonIconWrapper isExpanded={isExpanded}>
            {dnd ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M8.72 13.7508C8.85801 13.5118 9.16386 13.4301 9.40295 13.5679C9.64209 13.7059 9.72397 14.0117 9.58589 14.2508C9.425 14.5294 9.19393 14.7611 8.91532 14.922C8.63674 15.0828 8.32029 15.1674 7.99865 15.1675C7.67706 15.1674 7.36052 15.0828 7.08198 14.922C6.80344 14.7611 6.57161 14.5294 6.41076 14.2508C6.27293 14.0118 6.35478 13.7059 6.5937 13.5679C6.83275 13.4298 7.13848 13.5119 7.27664 13.7508C7.34973 13.8774 7.45545 13.9824 7.58198 14.0555C7.70855 14.1285 7.85251 14.1674 7.99865 14.1675C8.14477 14.1674 8.28876 14.1285 8.41532 14.0555C8.54183 13.9824 8.64693 13.8773 8.72 13.7508Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.978468 0.980622C1.17373 0.785462 1.49027 0.785412 1.6855 0.980622L15.0188 14.314C15.2141 14.5092 15.214 14.8257 15.0188 15.021C14.8236 15.2162 14.5071 15.2162 14.3118 15.021L11.125 11.8341H2.66532C2.4394 11.8341 2.21796 11.7686 2.0286 11.6453C1.8392 11.522 1.68971 11.3462 1.59826 11.1395C1.50686 10.9328 1.47746 10.7037 1.51362 10.4806C1.54989 10.2575 1.65059 10.0495 1.80269 9.88231L1.8131 9.8706C2.2567 9.41332 2.65941 8.9937 2.9661 8.34455C3.27218 7.69645 3.49864 6.78102 3.49865 5.33414C3.49858 4.99355 3.53744 4.65436 3.61388 4.32307L0.978468 1.68765C0.783226 1.49239 0.783212 1.17588 0.978468 0.980622ZM4.49865 5.33414C4.49864 6.88565 4.25566 7.95589 3.87039 8.77164C3.48876 9.57941 2.98195 10.101 2.54162 10.5548C2.51996 10.5787 2.50581 10.609 2.5006 10.6408C2.49548 10.6725 2.50002 10.7051 2.51297 10.7345C2.526 10.764 2.54723 10.7892 2.57417 10.8068C2.6012 10.8244 2.63308 10.8341 2.66532 10.8341H10.125L4.50256 5.21174C4.50112 5.25253 4.49864 5.29328 4.49865 5.33414Z"
                  fill="currentColor"
                />
                <path
                  d="M7.7786 0.839346C8.59236 0.799551 9.40191 0.981893 10.1204 1.36604C10.8388 1.7502 11.4399 2.32219 11.8587 3.02099C12.2774 3.71977 12.4986 4.5195 12.4986 5.33414C12.4986 7.02316 12.981 8.2379 13.552 9.08935C13.7056 9.31855 13.6443 9.62886 13.4153 9.78271C13.186 9.93652 12.8751 9.87532 12.7213 9.64599C12.0432 8.63481 11.4986 7.22741 11.4986 5.33414C11.4986 4.70058 11.3263 4.07878 11.0006 3.53531C10.6749 2.99181 10.2078 2.54634 9.64904 2.24755C9.09023 1.94878 8.46034 1.80709 7.82743 1.83804C7.19458 1.86903 6.58158 2.07149 6.05464 2.42333C5.82501 2.57654 5.51459 2.5149 5.36128 2.28531C5.20819 2.05569 5.27041 1.74523 5.49995 1.59195C6.17744 1.13962 6.96496 0.879176 7.7786 0.839346Z"
                  fill="currentColor"
                />
              </svg>
            ) : unreadNotifications > 0 ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M8.7207 13.7507C8.85882 13.5116 9.16457 13.4297 9.40365 13.5677C9.64258 13.7058 9.72451 14.0116 9.58659 14.2507C9.42575 14.5292 9.19452 14.761 8.91602 14.9219C8.63749 15.0827 8.32095 15.1672 7.99935 15.1673C7.67773 15.1673 7.36126 15.0826 7.08268 14.9219C6.80403 14.761 6.57237 14.5293 6.41146 14.2507C6.27338 14.0115 6.35527 13.7058 6.5944 13.5677C6.8335 13.4298 7.1393 13.5116 7.27734 13.7507C7.35048 13.8773 7.45604 13.9822 7.58268 14.0553C7.70929 14.1284 7.85318 14.1673 7.99935 14.1673C8.14542 14.1672 8.2895 14.1284 8.41602 14.0553C8.54242 13.9822 8.64767 13.8771 8.7207 13.7507Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.9987 0.833984C9.19205 0.834049 10.3371 1.30789 11.181 2.15169C12.0248 2.99559 12.4987 4.14057 12.4987 5.33398C12.4987 6.78133 12.7257 7.6969 13.0319 8.34505C13.3386 8.99404 13.7407 9.4132 14.1842 9.87044C14.1879 9.87422 14.1918 9.87827 14.1953 9.88216C14.3474 10.0494 14.4475 10.2573 14.4837 10.4805C14.5199 10.7036 14.4905 10.9326 14.3991 11.1393C14.3076 11.346 14.1581 11.5219 13.9687 11.6452C13.8031 11.753 13.6129 11.8165 13.4167 11.8307L13.332 11.834H2.66536C2.43939 11.8338 2.21794 11.7679 2.02865 11.6445C1.8393 11.521 1.68953 11.3449 1.59831 11.138C1.50712 10.9312 1.47851 10.7023 1.51497 10.4792C1.55149 10.256 1.65164 10.0479 1.80404 9.88086L1.81445 9.86979C2.25718 9.41272 2.6595 8.99354 2.96615 8.3444C3.27221 7.69631 3.4987 6.78095 3.4987 5.33398C3.4987 4.14051 3.97314 2.99561 4.81706 2.15169C5.6609 1.30805 6.80545 0.834028 7.9987 0.833984ZM7.9987 1.83398C7.07056 1.83403 6.18042 2.20311 5.52409 2.85937C4.86776 3.51574 4.4987 4.40577 4.4987 5.33398C4.4987 6.88587 4.25506 7.9558 3.86979 8.77148C3.48811 9.57947 2.98232 10.1011 2.54297 10.5547L2.51628 10.5944C2.50923 10.6089 2.50393 10.6246 2.5013 10.6406C2.49615 10.6724 2.50071 10.7049 2.51367 10.7344C2.52668 10.7638 2.54792 10.789 2.57487 10.8066C2.60189 10.8242 2.63378 10.834 2.66602 10.834H13.3327C13.3647 10.8339 13.3963 10.8247 13.4232 10.8073C13.4501 10.7897 13.472 10.7644 13.485 10.735C13.4981 10.7055 13.5019 10.6725 13.4967 10.6406C13.4916 10.6089 13.4773 10.5792 13.4557 10.5553C13.0155 10.1015 12.5092 9.57976 12.1276 8.77213C11.7422 7.95629 11.4987 6.88597 11.4987 5.33398C11.4987 4.4059 11.1301 3.51572 10.474 2.85937C9.81764 2.20305 8.92687 1.83405 7.9987 1.83398Z"
                  fill="currentColor"
                />
                <path
                  d="M2.25716 1.04557C2.41645 0.820124 2.72824 0.766577 2.95378 0.925781C3.17915 1.08509 3.23275 1.39688 3.07357 1.6224C2.32085 2.68884 1.83203 3.92361 1.83203 5.33398C1.83193 5.61004 1.60811 5.83398 1.33203 5.83398C1.05595 5.83398 0.832129 5.61004 0.832031 5.33398C0.832031 3.67788 1.41004 2.2457 2.25716 1.04557Z"
                  fill="currentColor"
                />
                <path
                  d="M13.0436 0.925781C13.2692 0.766577 13.5809 0.820124 13.7402 1.04557C14.5873 2.2457 15.1654 3.67788 15.1654 5.33398C15.1653 5.61004 14.9414 5.83397 14.6654 5.83398C14.3893 5.83398 14.1655 5.61004 14.1654 5.33398C14.1654 3.92362 13.6765 2.68884 12.9238 1.6224C12.7646 1.39688 12.8182 1.08508 13.0436 0.925781Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M8.7211 13.7507C8.85916 13.5116 9.16493 13.4298 9.40405 13.5677C9.64318 13.7058 9.72507 14.0115 9.58699 14.2507C9.4261 14.5292 9.19502 14.761 8.91642 14.9219C8.63782 15.0827 8.32142 15.1673 7.99975 15.1673C7.67809 15.1673 7.36167 15.0826 7.08308 14.9219C6.80444 14.761 6.57275 14.5293 6.41186 14.2507C6.27385 14.0115 6.35575 13.7058 6.5948 13.5677C6.83391 13.4296 7.13964 13.5116 7.27774 13.7507C7.35087 13.8773 7.45645 13.9822 7.58308 14.0553C7.70971 14.1284 7.85354 14.1673 7.99975 14.1673C8.1459 14.1673 8.28984 14.1284 8.41642 14.0553C8.54292 13.9822 8.64803 13.8772 8.7211 13.7507Z"
                  fill="currentColor"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.9991 0.833984C9.19249 0.833984 10.3375 1.30788 11.1814 2.15169C12.0253 2.99561 12.4991 4.14051 12.4991 5.33398C12.4991 6.78128 12.7261 7.6969 13.0323 8.34505C13.339 8.99406 13.7411 9.41321 14.1846 9.87044C14.1883 9.87424 14.1922 9.87825 14.1957 9.88216C14.3479 10.0494 14.4479 10.2573 14.4841 10.4805C14.5203 10.7036 14.4909 10.9326 14.3995 11.1393C14.308 11.346 14.1586 11.5219 13.9691 11.6452C13.8034 11.753 13.6133 11.8165 13.4171 11.8307L13.3324 11.834H2.66576C2.43978 11.8338 2.21833 11.768 2.02905 11.6445C1.83973 11.521 1.68991 11.3449 1.59871 11.138C1.50754 10.9312 1.47892 10.7022 1.51537 10.4792C1.55188 10.256 1.65207 10.0479 1.80444 9.88086L1.81485 9.86979C2.25761 9.41269 2.65987 8.99356 2.96655 8.3444C3.27263 7.6963 3.4991 6.78102 3.4991 5.33398C3.4991 4.14055 3.97359 2.9956 4.81746 2.15169C5.66127 1.30801 6.80586 0.834071 7.9991 0.833984ZM7.9991 1.83398C7.07096 1.83407 6.18079 2.20308 5.52449 2.85937C4.86821 3.51574 4.4991 4.4058 4.4991 5.33398C4.4991 6.88593 4.25548 7.95579 3.87019 8.77148C3.48848 9.5795 2.98274 10.1011 2.54337 10.5547L2.51668 10.5944C2.50964 10.6089 2.50433 10.6246 2.5017 10.6406C2.49656 10.6724 2.50113 10.7049 2.51407 10.7344C2.52706 10.7638 2.54835 10.789 2.57527 10.8066C2.60228 10.8243 2.63417 10.8339 2.66641 10.834H13.3331C13.3651 10.8339 13.3967 10.8247 13.4236 10.8073C13.4506 10.7897 13.4723 10.7645 13.4854 10.735C13.4985 10.7055 13.5023 10.6725 13.4971 10.6406C13.492 10.6088 13.4778 10.5792 13.4561 10.5553C13.0159 10.1015 12.5096 9.57976 12.128 8.77213C11.7426 7.95629 11.4991 6.88593 11.4991 5.33398C11.4991 4.40584 11.1306 3.51573 10.4744 2.85937C9.81799 2.20305 8.92732 1.83398 7.9991 1.83398Z"
                  fill="currentColor"
                />
              </svg>
            )}

            <ButtonText isActive={isActive} isExpanded={isExpanded}>
              {t('profile_page.tabs.notifications')}
            </ButtonText>
          </ButtonIconWrapper>

          {unreadNotifications > 0 && (
            <Badge
              badgeContent={unreadNotifications}
              // @ts-ignore
              color="accent"
              sx={{
                position: 'absolute',
                transition: 'all 0.4s linear',
                ...(isExpanded
                  ? { left: 'calc(100% - 4rem)', top: '1.25rem' }
                  : { left: '2rem', top: '0.5rem' }),

                '& > span': {
                  transition: 'all 0.3s linear',
                  fontSize: '0.625rem!important',
                  height: 'auto',
                  padding: isExpanded ? '0.25rem 0.625rem' : '0.25rem',
                },
              }}
              data-testid="menu-notifications-badge"
            />
          )}

          {isExpanded && (
            <IconButton sx={{ padding: 0 }}>
              <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          )}
        </ButtonLabel>
      </MenuButton>

      {createPortal(<NotificationToastContainer />, document.body)}

      {createPortal(
        <>
          {isDrawerOpen && (
            <Box
              onClick={() => setIsDrawerOpen(false)}
              sx={{ position: 'fixed', inset: 0, zIndex: 1200 }}
            />
          )}
          <Box
            sx={{
              position: 'fixed',
              zIndex: 1201,
              left: `${sidebarWidth}px`,
              right: 0,
              top: 0,
              bottom: 0,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: '2rem',
                bottom: '1rem',
                top: 'auto',
                height: isDrawerExpanded ? 'calc(100vh - 2rem)' : '36.875rem',
                maxHeight: 'calc(100vh - 2rem)',
                width: '26.25rem',
                maxWidth: `calc(100% - 3rem)`,
                padding: '0.125rem',
                borderRadius: '0.75rem',
                bgcolor: '#fff',
                boxShadow: isDrawerOpen ? '0px 1px 8px 0px rgba(0, 0, 0, 0.15)' : 'none',
                overflow: 'hidden',
                transform: isDrawerOpen ? 'translateX(0)' : 'translateX(calc(-100% - 2rem))',
                transition: [
                  isDrawerOpen
                    ? 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
                  isDrawerExpanded
                    ? 'height 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
                    : 'height 0.5s cubic-bezier(0.4, 0, 0.6, 1)',
                ].join(', '),
                pointerEvents: isDrawerOpen ? 'auto' : 'none',
              }}
            >
              {isDrawerOpen && <NotificationsSideMenu closeDrawer={() => setIsDrawerOpen(false)} />}
            </Box>
          </Box>
        </>,
        document.body,
      )}
    </>
  );
};

export default NotificationsButton;
